import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { BarChart2, Building2, Download, Search } from "lucide-react";
import { format } from "date-fns";
import { useBusinesses } from "../../../hooks/useBusinesses";
import { useAuth } from "../../../hooks/useAuth";
import { bizApi } from "../../../lib/bizApi";
import { cashAdvanceApi } from "../../../lib/cashAdvanceApi";
import type { CashAdvance, CashAdvanceStatus } from "../../../lib/cashAdvanceApi";
import { walletApi } from "../../../lib/walletApi";
import type {
  WalletEncashment, EncashmentStatus,
  WalletAdvance, WalletAdvanceStatus,
  ServiceRequest, ServiceRequestStatus,
  WalletCredit,
} from "../../../lib/walletApi";
import type { Loan, LoanStatus } from "../../../lib/loanApi";
import type { Leave, LeaveStatus } from "../../../lib/leaveApi";
import { storeApi } from "../../../lib/storeApi";
import type { StoreRecord, StoreService } from "../../../lib/storeApi";
import { LoadingSpinner } from "../../../components/ui/LoadingSpinner";
import { formatCurrency, formatDate } from "../../../utils/format";

type ReportType = "loan" | "cash-advance" | "wallet" | "leave" | "store";

// ── CSV helpers ────────────────────────────────────────────────────────────────

function escapeCSV(value: string | number): string {
  const s = String(value);
  if (s.includes(",") || s.includes('"') || s.includes("\n")) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

function downloadCSV(filename: string, headers: string[], rows: (string | number)[][]): void {
  const csv = [headers.map(escapeCSV).join(","), ...rows.map((r) => r.map(escapeCSV).join(","))].join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

// ── Shared helpers ─────────────────────────────────────────────────────────────

function fmtDate(s?: string): string {
  if (!s) return "—";
  return formatDate(s);
}

function StatusBadge({ status }: { status: string }) {
  const cls: Record<string, string> = {
    PENDING:  "bg-[#fef9c3] text-[#a16207]",
    APPROVED: "bg-[#dbeafe] text-[#1d4ed8]",
    ACTIVE:   "bg-[#dcfce7] text-[#15803d]",
    RELEASED: "bg-[#dcfce7] text-[#15803d]",
    PAID:     "bg-[#f3e8ff] text-[#7e22ce]",
    REJECTED: "bg-[#fee2e2] text-[#b91c1c]",
  };
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls[status] ?? "bg-[#f1f5f9] text-[#475569]"}`}>
      {status}
    </span>
  );
}

const TH = ({ children }: { children: string }) => (
  <th className="text-left px-4 py-3 text-xs font-semibold text-[#64748b] uppercase tracking-wide whitespace-nowrap">
    {children}
  </th>
);

// ── Tables ─────────────────────────────────────────────────────────────────────

function LoanTable({ loans }: { loans: Loan[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <TH>Employee</TH>
            <TH>Amount</TH>
            <TH>Term</TH>
            <TH>Progress</TH>
            <TH>Total Paid</TH>
            <TH>Status</TH>
            <TH>Applied At</TH>
            <TH>Starts</TH>
            <TH>Ends</TH>
            <TH>Release Date</TH>
            <TH>Disbursement Account</TH>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {loans.map((l) => (
            <tr key={l.loanId} className="hover:bg-[#f8fafc] transition-colors">
              {(() => {
                const showProgress = l.status === "ACTIVE" || l.status === "PAID";
                const totalPaid = showProgress && l.settledInstallments != null && l.installmentAmount != null
                  ? l.settledInstallments * l.installmentAmount
                  : null;
                return (
                  <>
                    <td className="px-4 py-3 text-[#0f172a] font-medium">{l.employeeName ?? "—"}</td>
                    <td className="px-4 py-3 text-[#0f172a] whitespace-nowrap">{formatCurrency(l.amount)}</td>
                    <td className="px-4 py-3 text-[#475569]">{l.termPeriods} periods</td>
                    <td className="px-4 py-3 text-[#475569] whitespace-nowrap font-mono text-xs">
                      {showProgress && l.totalInstallments != null
                        ? `${l.settledInstallments}/${l.totalInstallments}`
                        : "—"}
                    </td>
                    <td className="px-4 py-3 text-[#475569] whitespace-nowrap text-xs">
                      {totalPaid != null ? formatCurrency(totalPaid) : "—"}
                    </td>
                    <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
                    <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(l.createdAt)}</td>
                    <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{l.firstInstallmentPeriod ?? "—"}</td>
                    <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{l.lastInstallmentPeriod ?? "—"}</td>
                    <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{l.releaseDate ?? "—"}</td>
                    <td className="px-4 py-3 text-[#475569] text-xs">{l.disbursementAccount ?? "—"}</td>
                  </>
                );
              })()}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CashAdvanceTable({ items }: { items: CashAdvance[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <TH>Employee</TH>
            <TH>Amount</TH>
            <TH>Remaining</TH>
            <TH>Status</TH>
            <TH>Requested At</TH>
            <TH>Approved At</TH>
            <TH>Released At</TH>
            <TH>Notes</TH>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {items.map((c) => (
            <tr key={c.cashAdvId} className="hover:bg-[#f8fafc] transition-colors">
              <td className="px-4 py-3 text-[#0f172a] font-medium">{c.employeeName ?? "—"}</td>
              <td className="px-4 py-3 text-[#0f172a] whitespace-nowrap">{formatCurrency(c.amount)}</td>
              <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{formatCurrency(c.remainingAmount)}</td>
              <td className="px-4 py-3"><StatusBadge status={c.status} /></td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(c.requestedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(c.approvedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(c.releasedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs max-w-[160px] truncate">
                {c.releaseNote ?? c.rejectedReason ?? "—"}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function EncashmentTable({ items }: { items: WalletEncashment[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <TH>Employee</TH>
            <TH>Amount</TH>
            <TH>Status</TH>
            <TH>Requested At</TH>
            <TH>Approved By</TH>
            <TH>Approved At</TH>
            <TH>Released At</TH>
            <TH>Comments</TH>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {items.map((e) => (
            <tr key={e.encashId} className="hover:bg-[#f8fafc] transition-colors">
              <td className="px-4 py-3 text-[#0f172a] font-medium">{e.employeeName ?? "—"}</td>
              <td className="px-4 py-3 text-[#0f172a] whitespace-nowrap">{formatCurrency(e.amount)}</td>
              <td className="px-4 py-3"><StatusBadge status={e.status} /></td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(e.requestedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs">{e.approvedBy ?? "—"}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(e.approvedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(e.releasedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs max-w-[160px] truncate">{e.comments ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WalletAdvanceTable({ items }: { items: WalletAdvance[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <TH>Employee</TH>
            <TH>Amount</TH>
            <TH>Remaining</TH>
            <TH>Status</TH>
            <TH>Requested At</TH>
            <TH>Approved By</TH>
            <TH>Approved At</TH>
            <TH>Released By</TH>
            <TH>Released At</TH>
            <TH>Comments</TH>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {items.map((a) => (
            <tr key={a.advId} className="hover:bg-[#f8fafc] transition-colors">
              <td className="px-4 py-3 text-[#0f172a] font-medium">{a.employeeName ?? "—"}</td>
              <td className="px-4 py-3 text-[#0f172a] whitespace-nowrap">{formatCurrency(a.amount)}</td>
              <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{formatCurrency(a.remainingAmount)}</td>
              <td className="px-4 py-3"><StatusBadge status={a.status} /></td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(a.requestedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs">{a.approvedBy ?? "—"}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(a.approvedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs">{a.releasedBy ?? "—"}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(a.releasedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs max-w-[160px] truncate">{a.comments ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ServiceRequestTable({ items }: { items: ServiceRequest[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <TH>Employee</TH>
            <TH>Description</TH>
            <TH>Service Amount</TH>
            <TH>Commission</TH>
            <TH>Status</TH>
            <TH>Submitted At</TH>
            <TH>Reviewed By</TH>
            <TH>Reviewed At</TH>
            <TH>Notes</TH>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {items.map((r) => (
            <tr key={r.requestId} className="hover:bg-[#f8fafc] transition-colors">
              <td className="px-4 py-3 text-[#0f172a] font-medium">{r.employeeName ?? "—"}</td>
              <td className="px-4 py-3 text-[#0f172a]">{r.description}</td>
              <td className="px-4 py-3 text-[#475569] whitespace-nowrap">{formatCurrency(r.amount)}</td>
              <td className="px-4 py-3 text-[#16a34a] whitespace-nowrap font-medium">
                {r.commissionAmount != null ? formatCurrency(r.commissionAmount) : "—"}
              </td>
              <td className="px-4 py-3"><StatusBadge status={r.status} /></td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(r.createdAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs">{r.reviewedBy ?? "—"}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(r.reviewedAt)}</td>
              <td className="px-4 py-3 text-[#475569] text-xs max-w-[160px] truncate">{r.reviewNotes ?? r.notes ?? "—"}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function WalletCreditTable({ items }: { items: WalletCredit[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <TH>Employee</TH>
            <TH>Amount</TH>
            <TH>Reason</TH>
            <TH>Added By</TH>
            <TH>Added At</TH>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {items.map((c) => (
            <tr key={c.creditId} className="hover:bg-[#f8fafc] transition-colors">
              <td className="px-4 py-3 text-[#0f172a] font-medium">{c.employeeName ?? "—"}</td>
              <td className="px-4 py-3 text-[#2447d9] font-semibold whitespace-nowrap">{formatCurrency(c.amount)}</td>
              <td className="px-4 py-3 text-[#475569] max-w-[200px] truncate">{c.reason}</td>
              <td className="px-4 py-3 text-[#475569] text-xs">{c.addedBy}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(c.addedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function LeaveTable({ items }: { items: Leave[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <TH>Employee</TH>
            <TH>Type</TH>
            <TH>Start Date</TH>
            <TH>End Date</TH>
            <TH>Days</TH>
            <TH>Status</TH>
            <TH>Reason</TH>
            <TH>Reviewed By</TH>
            <TH>Reviewed At</TH>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {items.map((l) => (
            <tr key={l.leaveId} className="hover:bg-[#f8fafc] transition-colors">
              <td className="px-4 py-3 text-[#0f172a] font-medium">{l.employeeName ?? "—"}</td>
              <td className="px-4 py-3 text-[#475569]">{l.type}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{l.startDate}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{l.endDate}</td>
              <td className="px-4 py-3 text-[#475569] text-center">{l.days}</td>
              <td className="px-4 py-3"><StatusBadge status={l.status} /></td>
              <td className="px-4 py-3 text-[#475569] text-xs max-w-[180px] truncate" title={l.reason}>{l.reason}</td>
              <td className="px-4 py-3 text-[#475569] text-xs">{l.reviewedBy ?? "—"}</td>
              <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{fmtDate(l.reviewedAt)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ── CSV exports ────────────────────────────────────────────────────────────────

function exportLoans(loans: Loan[], bizName: string) {
  downloadCSV(
    `report-loan-${bizName}-${format(new Date(), "yyyyMMdd")}.csv`,
    ["Employee Name", "Amount", "Term (Periods)", "Progress", "Total Paid", "Status", "Applied At", "Installment Starts", "Installment Ends", "Release Date", "Disbursement Account"],
    loans.map((l) => {
      const showProgress = l.status === "ACTIVE" || l.status === "PAID";
      const totalPaid = showProgress && l.settledInstallments != null && l.installmentAmount != null
        ? l.settledInstallments * l.installmentAmount
        : "";
      return [
        l.employeeName ?? "",
        l.amount,
        l.termPeriods,
        showProgress && l.totalInstallments != null ? `${l.settledInstallments}/${l.totalInstallments}` : "",
        totalPaid,
        l.status,
        l.createdAt,
        l.firstInstallmentPeriod ?? "",
        l.lastInstallmentPeriod ?? "",
        l.releaseDate ?? "",
        l.disbursementAccount ?? "",
      ];
    }),
  );
}

function exportCashAdvances(items: CashAdvance[], bizName: string) {
  downloadCSV(
    `report-cash-advance-${bizName}-${format(new Date(), "yyyyMMdd")}.csv`,
    ["Employee Name", "Amount", "Remaining Amount", "Status", "Requested At", "Approved At", "Released At", "Notes"],
    items.map((c) => [
      c.employeeName ?? "",
      c.amount,
      c.remainingAmount,
      c.status,
      c.requestedAt,
      c.approvedAt ?? "",
      c.releasedAt ?? "",
      c.releaseNote ?? c.rejectedReason ?? "",
    ]),
  );
}

function exportEncashments(items: WalletEncashment[], bizName: string) {
  downloadCSV(
    `report-wallet-${bizName}-${format(new Date(), "yyyyMMdd")}.csv`,
    ["Employee Name", "Amount", "Status", "Requested At", "Approved By", "Approved At", "Released At", "Comments"],
    items.map((e) => [
      e.employeeName ?? "",
      e.amount,
      e.status,
      e.requestedAt,
      e.approvedBy ?? "",
      e.approvedAt ?? "",
      e.releasedAt ?? "",
      e.comments ?? "",
    ]),
  );
}

function exportWalletAdvances(items: WalletAdvance[], bizName: string) {
  downloadCSV(
    `report-wallet-advances-${bizName}-${format(new Date(), "yyyyMMdd")}.csv`,
    ["Employee", "Amount", "Remaining", "Status", "Requested At", "Approved By", "Approved At", "Released By", "Released At", "Comments"],
    items.map((a) => [
      a.employeeName ?? "", a.amount, a.remainingAmount, a.status,
      a.requestedAt, a.approvedBy ?? "", a.approvedAt ?? "",
      a.releasedBy ?? "", a.releasedAt ?? "", a.comments ?? "",
    ]),
  );
}

function exportServiceRequests(items: ServiceRequest[], bizName: string) {
  downloadCSV(
    `report-service-requests-${bizName}-${format(new Date(), "yyyyMMdd")}.csv`,
    ["Employee", "Description", "Service Amount", "Commission", "Status", "Submitted At", "Reviewed By", "Reviewed At", "Notes"],
    items.map((r) => [
      r.employeeName ?? "", r.description, r.amount,
      r.commissionAmount ?? "", r.status, r.createdAt,
      r.reviewedBy ?? "", r.reviewedAt ?? "", r.reviewNotes ?? r.notes ?? "",
    ]),
  );
}

function exportWalletCredits(items: WalletCredit[], bizName: string) {
  downloadCSV(
    `report-wallet-credits-${bizName}-${format(new Date(), "yyyyMMdd")}.csv`,
    ["Employee", "Amount", "Reason", "Added By", "Added At"],
    items.map((c) => [c.employeeName ?? "", c.amount, c.reason, c.addedBy, c.addedAt]),
  );
}

function exportLeaves(items: Leave[], bizName: string) {
  downloadCSV(
    `report-leave-${bizName}-${format(new Date(), "yyyyMMdd")}.csv`,
    ["Employee Name", "Type", "Start Date", "End Date", "Days", "Status", "Reason", "Reviewed By", "Reviewed At"],
    items.map((l) => [
      l.employeeName ?? "",
      l.type,
      l.startDate,
      l.endDate,
      l.days,
      l.status,
      l.reason,
      l.reviewedBy ?? "",
      l.reviewedAt ?? "",
    ]),
  );
}

function computeStoreCommission(record: StoreRecord, services: StoreService[]): number {
  const svc = services.find((s) => s.serviceId === record.serviceId);
  if (!svc) return 0;
  if (svc.commissionType === "fixed") return svc.commissionFixed ?? 0;
  const rate = svc.commissionRate ?? 0;
  return rate > 0 ? (record.price * rate) / 100 : 0;
}

function StoreReportTable({ records, services }: { records: StoreRecord[]; services: StoreService[] }) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="bg-[#f8fafc] border-b border-[#e2e8f0]">
            <TH>Date</TH>
            <TH>Service</TH>
            <TH>Staff</TH>
            <TH>Income</TH>
            <TH>Commission</TH>
            <TH>Created By</TH>
          </tr>
        </thead>
        <tbody className="divide-y divide-[#f1f5f9]">
          {records.map((r) => {
            const allStaff = [{ staffId: r.staffId, staffName: r.staffName }, ...(r.additionalStaff ?? [])];
            const totalComm = computeStoreCommission(r, services);
            const perStaff = allStaff.length > 0 ? totalComm / allStaff.length : 0;
            return (
              <tr key={r.recordId} className="hover:bg-[#f8fafc] transition-colors">
                <td className="px-4 py-3 text-[#475569] text-xs whitespace-nowrap">{formatDate(r.date)}</td>
                <td className="px-4 py-3 text-[#0f172a] font-medium">{r.serviceName}</td>
                <td className="px-4 py-3 text-[#475569]">
                  <div className="flex flex-col gap-0.5">
                    {allStaff.map((s, i) => (
                      <span key={s.staffId} className="whitespace-nowrap">
                        {s.staffName}
                        {i === 0 && allStaff.length > 1 && <span className="ml-1 text-[10px] text-[#94a3b8]">(primary)</span>}
                      </span>
                    ))}
                  </div>
                </td>
                <td className="px-4 py-3 text-[#0f172a] whitespace-nowrap">{formatCurrency(r.price)}</td>
                <td className="px-4 py-3">
                  {totalComm > 0 ? (
                    <div className="flex flex-col gap-0.5">
                      <span className="text-[#16a34a] font-medium whitespace-nowrap">{formatCurrency(totalComm)} total</span>
                      {allStaff.length > 1 && (
                        <span className="text-[10px] text-[#64748b] whitespace-nowrap">{formatCurrency(perStaff)} / staff</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-[#94a3b8]">—</span>
                  )}
                </td>
                <td className="px-4 py-3 text-[#475569] text-xs">{r.createdBy ?? "—"}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function exportStoreRecords(records: StoreRecord[], services: StoreService[], bizName: string) {
  downloadCSV(
    `report-store-${bizName}-${format(new Date(), "yyyyMMdd")}.csv`,
    ["Date", "Service", "Staff", "Income", "Total Commission", "Commission per Staff", "Created By"],
    records.map((r) => {
      const allStaff = [{ staffId: r.staffId, staffName: r.staffName }, ...(r.additionalStaff ?? [])];
      const totalComm = computeStoreCommission(r, services);
      const perStaff = allStaff.length > 0 ? totalComm / allStaff.length : 0;
      const staffNames = allStaff.map((s) => s.staffName).join(", ");
      return [r.date, r.serviceName, staffNames, r.price, totalComm > 0 ? totalComm : "", allStaff.length > 1 ? perStaff : "", r.createdBy ?? ""];
    }),
  );
}

// ── Constants ──────────────────────────────────────────────────────────────────

const REPORT_TABS: { type: ReportType; label: string }[] = [
  { type: "loan",         label: "Loan Report" },
  { type: "cash-advance", label: "Cash Advance" },
  { type: "wallet",       label: "Wallet" },
  { type: "leave",        label: "Leave Report" },
  { type: "store",        label: "Manage Store" },
];

const LOAN_STATUSES: Array<LoanStatus | "ALL"> = ["ALL", "PENDING", "APPROVED", "ACTIVE", "REJECTED", "PAID"];
const CA_STATUSES:   Array<CashAdvanceStatus | "ALL"> = ["ALL", "PENDING", "APPROVED", "RELEASED", "PAID", "REJECTED"];
const ENC_STATUSES:  Array<EncashmentStatus | "ALL">  = ["ALL", "PENDING", "APPROVED", "RELEASED", "REJECTED"];
const ADV_STATUSES:  Array<WalletAdvanceStatus | "ALL"> = ["ALL", "PENDING", "APPROVED", "RELEASED", "PAID", "REJECTED"];
const SVC_STATUSES:  Array<ServiceRequestStatus | "ALL"> = ["ALL", "PENDING", "APPROVED", "REJECTED"];
const LEAVE_STATUSES: Array<LeaveStatus | "ALL"> = ["ALL", "PENDING", "APPROVED", "REJECTED"];

type WalletSubReport = "encashments" | "advances" | "service-requests" | "credits";

// ── Page ───────────────────────────────────────────────────────────────────────

export default function ReportsPage() {
  const [reportType, setReportType]     = useState<ReportType>("loan");
  const [businessId, setBusinessId]     = useState("");
  const [loanStatus, setLoanStatus]     = useState<LoanStatus | "ALL">("ALL");
  const [caStatus, setCaStatus]         = useState<CashAdvanceStatus | "ALL">("ALL");
  const [encStatus, setEncStatus]       = useState<EncashmentStatus | "ALL">("ALL");
  const [walletSub, setWalletSub]       = useState<WalletSubReport>("encashments");
  const [advStatus, setAdvStatus]       = useState<WalletAdvanceStatus | "ALL">("ALL");
  const [svcStatus, setSvcStatus]       = useState<ServiceRequestStatus | "ALL">("ALL");
  const [leaveStatus, setLeaveStatus]   = useState<LeaveStatus | "ALL">("ALL");
  const [nameSearch, setNameSearch]     = useState("");
  const [storeStart, setStoreStart]     = useState(() => new Date().toISOString().slice(0, 7) + "-01");
  const [storeEnd, setStoreEnd]         = useState(() => new Date().toISOString().slice(0, 10));
  const [storeSearch, setStoreSearch]   = useState("");

  const { isAdmin, activeBusinessId, businesses: authBusinesses } = useAuth();

  // Super-admin: load all businesses from admin API (GET /admin/businesses).
  // Non-super-admin: disabled — use businesses from auth context (GET /me/businesses).
  const { data: bizData } = useBusinesses(isAdmin);
  const businesses = isAdmin ? (bizData?.items ?? []) : authBusinesses;
  const selectedBusiness = businesses.find((b) => b.businessId === businessId);

  // Non-super-admin users are scoped to their active business automatically.
  useEffect(() => {
    if (!isAdmin && activeBusinessId && !businessId) {
      setBusinessId(activeBusinessId);
    }
  }, [isAdmin, activeBusinessId, businessId]);

  // ── Queries ──────────────────────────────────────────────────────────────────

  const loanQuery = useQuery({
    queryKey: ["reports", "loan", businessId],
    queryFn: async () => {
      const statuses: LoanStatus[] = ["PENDING", "APPROVED", "ACTIVE", "REJECTED", "PAID"];
      const all: import("../../../lib/loanApi").Loan[] = [];
      for (const s of statuses) {
        try { const r = await bizApi.listLoans(businessId, s); all.push(...r.items); } catch { /* skip */ }
      }
      return { items: all };
    },
    enabled: reportType === "loan" && !!businessId,
    retry: 1,
  });

  const caQuery = useQuery({
    queryKey: ["reports", "cash-advance", businessId],
    queryFn: async () => {
      const statuses: CashAdvanceStatus[] = ["PENDING", "APPROVED", "RELEASED", "PAID", "REJECTED"];
      const all: import("../../../lib/cashAdvanceApi").CashAdvance[] = [];
      for (const s of statuses) {
        try { const r = await cashAdvanceApi.adminListCashAdvances(businessId, s, 100); all.push(...r.items); } catch { /* skip */ }
      }
      return all;
    },
    enabled: reportType === "cash-advance" && !!businessId,
    retry: 1,
  });

  // Sequential fetcher — avoids overwhelming Lambda concurrency with simultaneous requests.
  // Uses allSettled so a single 503 doesn't fail the whole report.
  async function fetchSequential<T, S extends string>(
    statuses: S[],
    fetcher: (s: S) => Promise<{ items: T[] }>,
  ): Promise<T[]> {
    const all: T[] = [];
    for (const s of statuses) {
      try {
        const r = await fetcher(s);
        all.push(...r.items);
      } catch {
        // skip failed status — partial data is better than no data
      }
    }
    return all;
  }

  const walletQuery = useQuery({
    queryKey: ["reports", "wallet", businessId],
    queryFn: () =>
      fetchSequential<WalletEncashment, EncashmentStatus>(
        ["PENDING", "APPROVED", "RELEASED", "REJECTED"],
        (s) => walletApi.listEncashments(businessId, s, { limit: 100 }),
      ),
    enabled: reportType === "wallet" && !!businessId,
    retry: 1,
  });

  const advancesReportQuery = useQuery({
    queryKey: ["reports", "wallet-advances", businessId],
    queryFn: () =>
      fetchSequential<WalletAdvance, WalletAdvanceStatus>(
        ["PENDING", "APPROVED", "RELEASED", "PAID", "REJECTED"],
        (s) => walletApi.listAdminWalletAdvances(businessId, s, { limit: 100 }),
      ),
    enabled: reportType === "wallet" && !!businessId,
    retry: 1,
  });

  const svcReportQuery = useQuery({
    queryKey: ["reports", "service-requests", businessId],
    queryFn: () =>
      fetchSequential<ServiceRequest, ServiceRequestStatus>(
        ["PENDING", "APPROVED", "REJECTED"],
        (s) => walletApi.adminListServiceRequests(businessId, s),
      ),
    enabled: reportType === "wallet" && !!businessId,
    retry: 1,
  });

  const creditsReportQuery = useQuery({
    queryKey: ["reports", "wallet-credits", businessId],
    queryFn: () => walletApi.listAdminWalletCredits(businessId, { limit: 200 }).then((r) => r.items),
    enabled: reportType === "wallet" && !!businessId,
  });

  const leaveQuery = useQuery({
    queryKey: ["reports", "leave", businessId],
    queryFn: async () => {
      const statuses: LeaveStatus[] = ["PENDING", "APPROVED", "REJECTED"];
      const all: Leave[] = [];
      for (const s of statuses) {
        try { const r = await bizApi.listLeaves(businessId, s); all.push(...r.items); } catch { /* skip */ }
      }
      return { items: all };
    },
    enabled: reportType === "leave" && !!businessId,
    retry: 1,
  });

  // Store report: storeId === businessId in this app (store is identified by its businessId).
  const storeYearPrefix = storeStart.slice(0, 4);
  const storeRecordsQuery = useQuery({
    queryKey: ["reports", "store", "records", businessId, storeYearPrefix],
    queryFn: () => storeApi.listRecords(businessId, storeYearPrefix).then((r) => r.items),
    enabled: reportType === "store" && !!businessId,
  });
  const storeServicesQuery = useQuery({
    queryKey: ["reports", "store", "services", businessId],
    queryFn: () => storeApi.listServices(businessId).then((r) => r.items),
    enabled: reportType === "store" && !!businessId,
  });

  // ── Filtered data ─────────────────────────────────────────────────────────────

  const filteredLoans = useMemo<Loan[]>(() => {
    const items = loanQuery.data?.items ?? [];
    return items
      .filter((l) => loanStatus === "ALL" || l.status === loanStatus)
      .filter((l) => !nameSearch || (l.employeeName ?? "").toLowerCase().includes(nameSearch.toLowerCase()));
  }, [loanQuery.data, loanStatus, nameSearch]);

  const filteredCa = useMemo<CashAdvance[]>(() => {
    const items = caQuery.data ?? [];
    return items.filter((c) => caStatus === "ALL" || c.status === caStatus);
  }, [caQuery.data, caStatus]);

  const filteredEnc = useMemo<WalletEncashment[]>(() => {
    const items = walletQuery.data ?? [];
    return items.filter((e) => encStatus === "ALL" || e.status === encStatus);
  }, [walletQuery.data, encStatus]);

  const filteredAdv = useMemo<WalletAdvance[]>(() => {
    const items = advancesReportQuery.data ?? [];
    return items.filter((a) => advStatus === "ALL" || a.status === advStatus);
  }, [advancesReportQuery.data, advStatus]);

  const filteredSvc = useMemo<ServiceRequest[]>(() => {
    const items = svcReportQuery.data ?? [];
    return items.filter((r) => svcStatus === "ALL" || r.status === svcStatus);
  }, [svcReportQuery.data, svcStatus]);

  const filteredCredits = creditsReportQuery.data ?? [];

  const filteredLeaves = useMemo<Leave[]>(() => {
    const items = leaveQuery.data?.items ?? [];
    return items
      .filter((l) => leaveStatus === "ALL" || l.status === leaveStatus)
      .filter((l) => !nameSearch || (l.employeeName ?? "").toLowerCase().includes(nameSearch.toLowerCase()));
  }, [leaveQuery.data, leaveStatus, nameSearch]);

  const storeServices = storeServicesQuery.data ?? [];
  const filteredStoreRecords = useMemo<StoreRecord[]>(() => {
    const items = storeRecordsQuery.data ?? [];
    return items
      .filter((r) => r.date >= storeStart && r.date <= storeEnd)
      .filter((r) => !storeSearch || r.staffName.toLowerCase().includes(storeSearch.toLowerCase()) || r.serviceName.toLowerCase().includes(storeSearch.toLowerCase()));
  }, [storeRecordsQuery.data, storeStart, storeEnd, storeSearch]);

  // ── Derived state ─────────────────────────────────────────────────────────────

  const walletSubLoading =
    walletSub === "encashments"      ? walletQuery.isLoading       :
    walletSub === "advances"         ? advancesReportQuery.isLoading :
    walletSub === "service-requests" ? svcReportQuery.isLoading     :
    creditsReportQuery.isLoading;

  const isLoading =
    (reportType === "loan"         && loanQuery.isLoading)  ||
    (reportType === "cash-advance" && caQuery.isLoading)    ||
    (reportType === "wallet"       && walletSubLoading)     ||
    (reportType === "leave"        && leaveQuery.isLoading) ||
    (reportType === "store"        && (storeRecordsQuery.isLoading || storeServicesQuery.isLoading));

  const hasError =
    (reportType === "loan"         && loanQuery.isError)  ||
    (reportType === "cash-advance" && caQuery.isError)    ||
    (reportType === "wallet"       && (walletQuery.isError || advancesReportQuery.isError || svcReportQuery.isError)) ||
    (reportType === "leave"        && leaveQuery.isError) ||
    (reportType === "store"        && storeRecordsQuery.isError);

  const walletSubRowCount =
    walletSub === "encashments"      ? filteredEnc.length     :
    walletSub === "advances"         ? filteredAdv.length     :
    walletSub === "service-requests" ? filteredSvc.length     :
    filteredCredits.length;

  const rowCount =
    reportType === "loan"         ? filteredLoans.length  :
    reportType === "cash-advance" ? filteredCa.length     :
    reportType === "wallet"       ? walletSubRowCount      :
    reportType === "store"        ? filteredStoreRecords.length :
    filteredLeaves.length;

  const bizName = selectedBusiness?.name ?? businessId;

  function handleExport() {
    if (reportType === "loan")         exportLoans(filteredLoans, bizName);
    else if (reportType === "cash-advance") exportCashAdvances(filteredCa, bizName);
    else if (reportType === "wallet") {
      if (walletSub === "encashments")      exportEncashments(filteredEnc, bizName);
      else if (walletSub === "advances")    exportWalletAdvances(filteredAdv, bizName);
      else if (walletSub === "service-requests") exportServiceRequests(filteredSvc, bizName);
      else                                  exportWalletCredits(filteredCredits, bizName);
    }
    else if (reportType === "store")    exportStoreRecords(filteredStoreRecords, storeServices, bizName);
    else                               exportLeaves(filteredLeaves, bizName);
  }

  const isStoreReport = reportType === "store";
  const isWalletReport = reportType === "wallet";
  const showNameSearch = reportType === "loan" || reportType === "leave";

  const walletSubStatusOptions =
    walletSub === "encashments"      ? ENC_STATUSES :
    walletSub === "advances"         ? ADV_STATUSES :
    walletSub === "service-requests" ? SVC_STATUSES : null; // credits have no status filter

  const statusOptions =
    reportType === "loan"         ? LOAN_STATUSES  :
    reportType === "cash-advance" ? CA_STATUSES    :
    reportType === "wallet"       ? (walletSubStatusOptions ?? []) :
    LEAVE_STATUSES;

  function getCurrentStatus(): string {
    if (reportType === "loan")         return loanStatus;
    if (reportType === "cash-advance") return caStatus;
    if (reportType === "wallet") {
      if (walletSub === "encashments")      return encStatus;
      if (walletSub === "advances")         return advStatus;
      if (walletSub === "service-requests") return svcStatus;
    }
    return leaveStatus;
  }

  function setCurrentStatus(val: string) {
    if (reportType === "loan")         setLoanStatus(val as LoanStatus | "ALL");
    else if (reportType === "cash-advance") setCaStatus(val as CashAdvanceStatus | "ALL");
    else if (reportType === "wallet") {
      if (walletSub === "encashments")      setEncStatus(val as EncashmentStatus | "ALL");
      else if (walletSub === "advances")    setAdvStatus(val as WalletAdvanceStatus | "ALL");
      else if (walletSub === "service-requests") setSvcStatus(val as ServiceRequestStatus | "ALL");
    }
    else setLeaveStatus(val as LeaveStatus | "ALL");
  }

  function handleTabChange(t: ReportType) {
    setReportType(t);
    setNameSearch("");
    if (t === "wallet") setWalletSub("encashments");
  }

  return (
    <div className="space-y-5">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#2447d9]">
            <BarChart2 className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-[#0f172a]">Reports</h1>
            <p className="text-xs text-[#64748b]">Export and review records across all report types</p>
          </div>
        </div>
        {businessId && rowCount > 0 && (
          <button
            onClick={handleExport}
            className="flex items-center gap-1.5 rounded-lg bg-[#2447d9] px-3 py-2 text-sm font-medium text-white hover:bg-[#1d3bb5] transition-colors"
          >
            <Download className="h-4 w-4" />
            Export CSV
          </button>
        )}
      </div>

      {/* Report type tabs */}
      <div className="flex gap-1 rounded-xl border border-[#e2e8f0] bg-white p-1 shadow-sm w-fit">
        {REPORT_TABS.map((tab) => (
          <button
            key={tab.type}
            onClick={() => handleTabChange(tab.type)}
            className={`rounded-lg px-4 py-2 text-sm font-medium transition-colors ${
              reportType === tab.type
                ? "bg-[#2447d9] text-white shadow-sm"
                : "text-[#475569] hover:bg-[#f8fafc] hover:text-[#0f172a]"
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Business selector — super-admin only; biz-scoped users auto-use their active business */}
      {isAdmin ? (
        <div className="flex items-center gap-2 rounded-xl border border-[#e2e8f0] bg-white px-3 py-2.5 shadow-sm">
          <Building2 className="h-4 w-4 shrink-0 text-[#2447d9]" />
          <select
            value={businessId}
            onChange={(e) => setBusinessId(e.target.value)}
            className="flex-1 min-w-0 bg-transparent text-sm font-medium text-[#0f172a] focus:outline-none cursor-pointer"
          >
            <option value="">Select a business to load report…</option>
            {businesses.map((b) => (
              <option key={b.businessId} value={b.businessId}>{b.name}</option>
            ))}
          </select>
          {businessId && (
            <button
              onClick={() => setBusinessId("")}
              className="shrink-0 text-xs text-[#94a3b8] hover:text-[#475569] transition-colors"
            >
              Clear
            </button>
          )}
        </div>
      ) : selectedBusiness && (
        <div className="flex items-center gap-2 rounded-xl border border-[#e2e8f0] bg-white px-3 py-2.5 shadow-sm">
          <Building2 className="h-4 w-4 shrink-0 text-[#2447d9]" />
          <span className="text-sm font-medium text-[#0f172a]">{selectedBusiness.name}</span>
        </div>
      )}

      {/* Wallet sub-tabs — shown only for the Wallet report */}
      {businessId && isWalletReport && (
        <div className="flex gap-1 rounded-lg bg-[#f1f5f9] p-1 w-fit overflow-x-auto overflow-y-hidden">
          {([
            { key: "encashments"      as WalletSubReport, label: "Encashment Requests" },
            { key: "advances"         as WalletSubReport, label: "Wallet Advances"     },
            { key: "service-requests" as WalletSubReport, label: "Service Requests"    },
            { key: "credits"          as WalletSubReport, label: "Wallet Credits"      },
          ]).map(({ key, label }) => (
            <button key={key} onClick={() => setWalletSub(key)}
              className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors whitespace-nowrap ${
                walletSub === key ? "bg-white text-[#0f172a] shadow-sm" : "text-[#64748b] hover:text-[#0f172a]"
              }`}
            >
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Filters — only shown once a business is selected */}
      {businessId && (
        <div className="flex flex-wrap items-center gap-3">
          {isStoreReport ? (
            <>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-[#64748b] whitespace-nowrap">From</label>
                <input type="date" value={storeStart} onChange={(e) => setStoreStart(e.target.value)}
                  className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]" />
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-[#64748b] whitespace-nowrap">To</label>
                <input type="date" value={storeEnd} min={storeStart} onChange={(e) => setStoreEnd(e.target.value)}
                  className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]" />
              </div>
              <div className="flex items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2">
                <Search className="h-4 w-4 text-[#94a3b8] shrink-0" />
                <input type="text" value={storeSearch} onChange={(e) => setStoreSearch(e.target.value)}
                  placeholder="Search staff or service…"
                  className="text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none w-44" />
              </div>
            </>
          ) : (
            <>
              {/* Status filter — hidden for Wallet Credits (no status field) */}
              {!(isWalletReport && walletSub === "credits") && statusOptions.length > 0 && (
                <div className="flex items-center gap-2">
                  <label className="text-xs font-medium text-[#64748b] whitespace-nowrap">Status</label>
                  <select
                    value={getCurrentStatus()}
                    onChange={(e) => setCurrentStatus(e.target.value)}
                    className="rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm focus:border-[#2447d9] focus:outline-none focus:ring-1 focus:ring-[#2447d9]"
                  >
                    {statusOptions.map((s) => (
                      <option key={s} value={s}>{s === "ALL" ? "All Statuses" : s}</option>
                    ))}
                  </select>
                </div>
              )}

              {/* Name search — only for loan and leave */}
              {showNameSearch && (
                <div className="flex items-center gap-2 rounded-lg border border-[#e2e8f0] bg-white px-3 py-2">
                  <Search className="h-4 w-4 text-[#94a3b8] shrink-0" />
                  <input
                    type="text"
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    placeholder="Search employee…"
                    className="text-sm text-[#0f172a] placeholder:text-[#94a3b8] focus:outline-none w-44"
                  />
                </div>
              )}
            </>
          )}

          {rowCount > 0 && (
            <span className="text-xs text-[#64748b] ml-auto">{rowCount} record{rowCount !== 1 ? "s" : ""}</span>
          )}
        </div>
      )}

      {/* Table card */}
      {!businessId ? (
        <div className="rounded-xl border border-[#e2e8f0] bg-white shadow-sm py-16 text-center">
          <BarChart2 className="h-10 w-10 text-[#cbd5e1] mx-auto mb-3" />
          <p className="text-sm font-medium text-[#64748b]">Select a business to load the report</p>
          <p className="text-xs text-[#94a3b8] mt-1">Data is scoped per business</p>
        </div>
      ) : (
        <div className="rounded-xl border border-[#e2e8f0] bg-white shadow-sm overflow-hidden">
          {isLoading ? (
            <div className="flex justify-center py-16"><LoadingSpinner /></div>
          ) : hasError ? (
            <div className="py-12 text-center text-[#64748b] text-sm">
              Failed to load report data. Please try again.
            </div>
          ) : rowCount === 0 ? (
            <div className="py-16 text-center">
              <BarChart2 className="h-10 w-10 text-[#cbd5e1] mx-auto mb-3" />
              <p className="text-sm font-medium text-[#64748b]">No records found</p>
              <p className="text-xs text-[#94a3b8] mt-1">Try adjusting the status filter or search term</p>
            </div>
          ) : (
            <>
              {reportType === "loan"         && <LoanTable loans={filteredLoans} />}
              {reportType === "cash-advance" && <CashAdvanceTable items={filteredCa} />}
              {reportType === "wallet" && walletSub === "encashments"      && <EncashmentTable items={filteredEnc} />}
              {reportType === "wallet" && walletSub === "advances"         && <WalletAdvanceTable items={filteredAdv} />}
              {reportType === "wallet" && walletSub === "service-requests" && <ServiceRequestTable items={filteredSvc} />}
              {reportType === "wallet" && walletSub === "credits"          && <WalletCreditTable items={filteredCredits} />}
              {reportType === "leave"        && <LeaveTable items={filteredLeaves} />}
              {reportType === "store"        && <StoreReportTable records={filteredStoreRecords} services={storeServices} />}
            </>
          )}
        </div>
      )}
    </div>
  );
}
